import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

function AdminPrograms() {
  const navigate = useNavigate();
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    duration: '',
    level: 'Beginner',
    price: 0,
    currency: 'USD',
    category: 'Web Development',
    tags: '',
    sections: []
  });
  const [sectionInput, setSectionInput] = useState({ name: '', order: 1 });

  useEffect(() => {
    fetchPrograms();
  }, []);

  const fetchPrograms = async () => {
    try {
      const res = await api.get('/admin/programs');
      setPrograms(res.data);
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.status === 401) navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const data = {
        ...formData,
        tags: formData.tags.split(',').map(t => t.trim()).filter(t => t)
      };

      if (editingProgram) {
        await api.put(`/admin/programs/${editingProgram._id}`, data);
      } else {
        await api.post('/admin/programs', data);
      }

      setShowModal(false);
      resetForm();
      fetchPrograms();
    } catch (error) {
      alert(error.response?.data?.message || 'Error saving program');
    }
  };

  const handleEdit = (program) => {
    setEditingProgram(program);
    setFormData({
      title: program.title,
      description: program.description,
      duration: program.duration,
      level: program.level,
      price: program.price,
      currency: program.currency,
      category: program.category,
      tags: program.tags?.join(', ') || '',
      sections: program.sections || []
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this program?')) return;
    
    try {
      await api.delete(`/admin/programs/${id}`);
      fetchPrograms();
    } catch (error) {
      alert(error.response?.data?.message || 'Error deleting program');
    }
  };

  const toggleActive = async (id) => {
    try {
      await api.patch(`/admin/programs/${id}/toggle-active`);
      fetchPrograms();
    } catch (error) {
      alert(error.response?.data?.message || 'Error updating program');
    }
  };

  const addSection = () => {
    if (!sectionInput.name.trim()) return;
    
    setFormData({
      ...formData,
      sections: [...formData.sections, { ...sectionInput }]
    });
    setSectionInput({ name: '', order: formData.sections.length + 2 });
  };

  const removeSection = (index) => {
    setFormData({
      ...formData,
      sections: formData.sections.filter((_, i) => i !== index)
    });
  };

  const resetForm = () => {
    setEditingProgram(null);
    setFormData({
      title: '',
      description: '',
      duration: '',
      level: 'Beginner',
      price: 0,
      currency: 'USD',
      category: 'Web Development',
      tags: '',
      sections: []
    });
    setSectionInput({ name: '', order: 1 });
  };

  return (
    <div className="container-fluid mt-4">
      <div className="row mb-3">
        <div className="col">
          <h2>Program Management</h2>
        </div>
        <div className="col-auto">
          <button 
            className="btn btn-primary"
            onClick={() => { resetForm(); setShowModal(true); }}
          >
            + Add Program
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center"><div className="spinner-border"></div></div>
      ) : (
        <div className="table-responsive">
          <table className="table table-striped table-hover">
            <thead>
              <tr>
                <th>Title</th>
                <th>Level</th>
                <th>Duration</th>
                <th>Sections</th>
                <th>Enrollments</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {programs.map((program) => (
                <tr key={program._id}>
                  <td><strong>{program.title}</strong></td>
                  <td><span className="badge bg-info">{program.level}</span></td>
                  <td>{program.duration}</td>
                  <td>{program.sections?.length || 0}</td>
                  <td>{program.enrollmentCount || 0}</td>
                  <td>
                    <span className={`badge bg-${program.isActive ? 'success' : 'secondary'}`}>
                      {program.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <button 
                      className="btn btn-sm btn-warning me-1"
                      onClick={() => handleEdit(program)}
                    >
                      Edit
                    </button>
                    <button 
                      className="btn btn-sm btn-info me-1"
                      onClick={() => toggleActive(program._id)}
                    >
                      {program.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                    <button 
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDelete(program._id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingProgram ? 'Edit' : 'Add'} Program</h5>
                <button className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Title *</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Description *</label>
                    <textarea 
                      className="form-control" 
                      rows="3"
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      required
                    ></textarea>
                  </div>
                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Duration *</label>
                      <input 
                        type="text" 
                        className="form-control" 
                        placeholder="e.g., 8 weeks"
                        value={formData.duration}
                        onChange={(e) => setFormData({...formData, duration: e.target.value})}
                        required
                      />
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Level</label>
                      <select 
                        className="form-select"
                        value={formData.level}
                        onChange={(e) => setFormData({...formData, level: e.target.value})}
                      >
                        <option>Beginner</option>
                        <option>Intermediate</option>
                        <option>Advanced</option>
                      </select>
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Category</label>
                      <input 
                        type="text" 
                        className="form-control"
                        value={formData.category}
                        onChange={(e) => setFormData({...formData, category: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Price</label>
                      <input 
                        type="number" 
                        className="form-control"
                        value={formData.price}
                        onChange={(e) => setFormData({...formData, price: e.target.value})}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Currency</label>
                      <input 
                        type="text" 
                        className="form-control"
                        value={formData.currency}
                        onChange={(e) => setFormData({...formData, currency: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Tags (comma-separated)</label>
                    <input 
                      type="text" 
                      className="form-control"
                      placeholder="react, javascript, frontend"
                      value={formData.tags}
                      onChange={(e) => setFormData({...formData, tags: e.target.value})}
                    />
                  </div>

                  <hr />
                  <h6>Sections</h6>
                  <div className="row mb-2">
                    <div className="col-md-8">
                      <input 
                        type="text" 
                        className="form-control form-control-sm"
                        placeholder="Section name"
                        value={sectionInput.name}
                        onChange={(e) => setSectionInput({...sectionInput, name: e.target.value})}
                      />
                    </div>
                    <div className="col-md-2">
                      <input 
                        type="number" 
                        className="form-control form-control-sm"
                        placeholder="Order"
                        value={sectionInput.order}
                        onChange={(e) => setSectionInput({...sectionInput, order: parseInt(e.target.value)})}
                      />
                    </div>
                    <div className="col-md-2">
                      <button type="button" className="btn btn-sm btn-success w-100" onClick={addSection}>
                        Add
                      </button>
                    </div>
                  </div>
                  <ul className="list-group">
                    {formData.sections.map((section, index) => (
                      <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                        <span><strong>#{section.order}</strong> - {section.name}</span>
                        <button 
                          type="button" 
                          className="btn btn-sm btn-danger"
                          onClick={() => removeSection(index)}
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingProgram ? 'Update' : 'Create'} Program
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPrograms;
